import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { IconButton, Icon } from '@material-ui/core';
import classNames from 'classnames';
import { getPaginationArray } from './helpers';

const useStyles = makeStyles(theme => ({
  root: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
  },
  paginationContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    '& button': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      border: 'none',
      fontWeight: 500,
      textTransform: 'uppercase',
      height: '24px',
      minWidth: '24px',
      borderRadius: '24px',
      lineHeight: '24px',
      color: theme.palette.text.common,
      letterSpacing: '.03em',
      margin: '0 2px',
      verticalAlign: 'middle',

      '&:focus': {
        outline: 'none',
      },

      '&.activePage': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
      },
    },
  },
  paginationItem: {
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
  },
  paginationDots: {
    fontSize: '16px',
    padding: 0,
  },
}));

const AppTablePagination = ({ count, page, rowsPerPage, onChangePage }) => {
  const classes = useStyles();
  const theme = useTheme();

  const safeCount = Number.isFinite(count) && count > 0 ? count : 0;
  const safeRowsPerPage = Number.isFinite(rowsPerPage) && rowsPerPage > 0 ? rowsPerPage : 1;
  const safePage = Number.isFinite(page) && page >= 0 ? page : 0;
  const pagesCount = Math.ceil(safeCount / safeRowsPerPage);
  const paginationArray = getPaginationArray(safePage + 1, pagesCount).filter(pageNumber =>
    Number.isFinite(pageNumber),
  );

  const handleBackButtonClick = event => {
    onChangePage(event, safePage - 1);
  };

  const handleNextButtonClick = event => {
    onChangePage(event, safePage + 1);
  };

  const handlePageClick = event => {
    const pageValue = Number(event.currentTarget.dataset.page);
    if (!Number.isFinite(pageValue)) {
      return;
    }
    onChangePage(event, pageValue - 1);
  };

  return (
    <div className={classes.root}>
      <IconButton onClick={handleBackButtonClick} disabled={safePage === 0} color="primary" size="small">
        {theme.direction === 'rtl' ? <Icon>keyboard_arrow_right</Icon> : <Icon>keyboard_arrow_left</Icon>}
      </IconButton>
      <div className={classes.paginationContainer}>
        {paginationArray.map((pageNumber, index) => {
          const dots = (
            <button type="button" className={classes.paginationDots}>
              &hellip;
            </button>
          );
          const pageButton = (
            <button
              type="button"
              key={pageNumber}
              data-page={pageNumber}
              className={classNames({
                [`${classes.paginationItem}`]: true,
                activePage: pageNumber === safePage + 1,
              })}
              onClick={handlePageClick}
            >
              {pageNumber}
            </button>
          );

          if (index === 1 && pageNumber > 2) {
            return (
              <React.Fragment key={pageNumber}>
                {dots}
                {pageButton}
              </React.Fragment>
            );
          }

          if (index === paginationArray.length - 2 && pageNumber < pagesCount - 1) {
            return (
              <React.Fragment key={pageNumber}>
                {pageButton}
                {dots}
              </React.Fragment>
            );
          }

          return pageButton;
        })}
      </div>
      <IconButton onClick={handleNextButtonClick} disabled={safePage >= pagesCount - 1} color="primary" size="small">
        {theme.direction === 'rtl' ? <Icon>keyboard_arrow_left</Icon> : <Icon>keyboard_arrow_right</Icon>}
      </IconButton>
    </div>
  );
};

AppTablePagination.propTypes = {
  count: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  onChangePage: PropTypes.func.isRequired,
};

export default AppTablePagination;
